import React, { useEffect, useRef, useState, useCallback } from 'react';
import './GoogleButton.css';
import { googleAuthService, GoogleUser } from '../services/googleAuth';

interface GoogleButtonProps {
  onLoginSuccess?: (user: GoogleUser) => void;
  onLoginError?: (error: any) => void;
}

const GoogleButton: React.FC<GoogleButtonProps> = ({ 
  onLoginSuccess, 
  onLoginError 
}) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);

  const initializeGoogleAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      // Check if Google Client ID is configured
      if (!googleAuthService.isConfigured()) {
        setIsConfigured(false);
        setIsLoading(false);
        return;
      }

      // Initialize Google Auth Service
      await googleAuthService.initialize();
      // Render Google login button
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = ''; // Clear container
        googleAuthService.renderButton(googleButtonRef.current);
      }
      
      setIsConfigured(true);
    } catch (error) {
      console.error('Failed to initialize Google Auth:', error);
      setIsConfigured(false);
      onLoginError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [onLoginError]);

  useEffect(() => {
    initializeGoogleAuth();
    
    // Listen for login success event
    const handleLoginSuccess = (event: CustomEvent) => {
      console.log('Google login successful:', event.detail);
      onLoginSuccess?.(event.detail);
    };

    // Listen for login error event
    const handleLoginError = (event: CustomEvent) => {
      console.error('Google login failed:', event.detail);
      onLoginError?.(event.detail);
    };

    window.addEventListener('googleLoginSuccess', handleLoginSuccess as EventListener);
    window.addEventListener('googleLoginError', handleLoginError as EventListener);

    return () => {
      window.removeEventListener('googleLoginSuccess', handleLoginSuccess as EventListener);
      window.removeEventListener('googleLoginError', handleLoginError as EventListener);
    };
  }, [initializeGoogleAuth, onLoginSuccess, onLoginError]);



  const handleFallbackLogin = () => {
    alert('Please configure Google Client ID first.\n\n1. Create OAuth 2.0 credentials in Google Cloud Console\n2. Create .env.local file\n3. Add REACT_APP_GOOGLE_CLIENT_ID=your_client_id');
  };

  // Show loading state if loading
  if (isLoading) {
    return (
      <button className="google-button" disabled>
        <div className="google-button-content">
          <div className="google-icon">
            <div className="loading-spinner"></div>
          </div>
          <span className="google-button-text">Loading Google...</span>
        </div>
      </button>
    );
  }

  // Show configuration prompt button if not configured
  if (!isConfigured) {
    return (
      <button className="google-button google-button-unconfigured" onClick={handleFallbackLogin}>
        <div className="google-button-content">
          <div className="google-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.68 10.83C4.68 10.06 4.83 9.32 5.1 8.64L1.76 6.09C1.05 7.62 0.64 9.31 0.64 11.11C0.64 12.91 1.05 14.6 1.76 16.13L5.1 13.58C4.83 12.9 4.68 12.16 4.68 11.39V10.83Z" fill="#FBBC05"/>
              <path d="M10.2 4.32C12.17 4.32 13.97 5.02 15.38 6.16L18.22 3.32C16.31 1.55 13.85 0.5 10.2 0.5C6.8 0.5 3.86 2.24 2.24 4.91L5.58 7.46C6.46 5.05 8.18 4.32 10.2 4.32Z" fill="#EA4335"/>
              <path d="M10.2 15.68C8.18 15.68 6.46 14.95 5.58 12.54L2.24 15.09C3.86 17.76 6.8 19.5 10.2 19.5C13.74 19.5 16.64 18.26 18.49 15.92L15.24 13.46C14.12 14.62 12.64 15.68 10.2 15.68Z" fill="#34A853"/>
              <path d="M19.36 10C19.36 9.34 19.3 8.68 19.18 8.05H10.2V11.95H15.31C15.05 13.17 14.36 14.23 13.24 14.95L15.24 16.41C17.68 14.17 19.36 12.23 19.36 10Z" fill="#4285F4"/>
            </svg>
          </div>
          <span className="google-button-text">Configure Google Login</span>
        </div>
      </button>
    );
  }

  // Render Google official button container
  return (
    <div className="google-button-container">
      <div ref={(node) => {
          if (node && isConfigured && !isLoading) {
            // DOM 挂载完成，且 Google Auth 已配置
            node.innerHTML = ''; // 清空
            googleAuthService.renderButton(node);
          }
          // 将节点赋值给原来的 ref
          (googleButtonRef as any).current = node;
        }} className="google-official-button"></div>
    </div>
  );
};

export default GoogleButton;
